import PlatformConsole from "./pages/PlatformConsole";
import ContractSign from "./pages/ContractSign";
import "./App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, useLocation, Routes, Route } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { NativeSelectSearch } from "@/components/NativeSelectSearch";
import { ActionGuard } from "@/components/ActionGuard";
import { WaExportPrompt } from "@/components/WaExportPrompt";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { api } from "./lib/api";
import { LanguageProvider, useLang } from "./i18n/LanguageContext";
import Landing from "./pages/Landing";
import StepUpGate from "./pages/StepUpGate";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Paywall from "./pages/Paywall";
import Onboarding from "./pages/Onboarding";
import PricingPage from "./pages/PricingPage";
import PublicCustody from "./pages/PublicCustody";
import ScanConsume from "./pages/ScanConsume";
import EmployeePortal from "./pages/EmployeePortal";
import EmployeeLanding from "./pages/EmployeeLanding";
import MarketerPortal from "./pages/MarketerPortal";
import MarketerLanding from "./pages/MarketerLanding";
import InsurerPortal from "./pages/InsurerPortal";
import FinanceApp from "./finance/FinanceApp";
import PaymentResult from "./pages/PaymentResult";
import PayCheckout from "./pages/PayCheckout";
import InfoPage from "./pages/InfoPage";
import MotoMaintenancePage from "./pages/MotoMaintenancePage";
import DemoEntry from "./pages/DemoEntry";
import CorrespondencePortal from "./pages/CorrespondencePortal";
import WarehousePortal from "./pages/WarehousePortal";
import WorkshopPortal from "./pages/WorkshopPortal";
import PhoneGate from "./components/PhoneGate";
import ErrorBoundary from "./components/ErrorBoundary";
import AIAssistant from "./components/AIAssistant";
import { TutorialVideo } from "./components/TutorialVideo";

const AssistantGate = () => (/^\/(contract\/|platform|workshop\/)/.test(useLocation().pathname) ? null : <AIAssistant />);
const TutorialGate = () => (useLocation().pathname.startsWith("/platform") ? null : <TutorialVideo />);
import { PublicRiderCard } from "./components/PortalRiderCard";
import GuestMeet from "./pages/GuestMeet";
import VerifyLetter from "./pages/VerifyLetter";
import { VideoCallHost } from "./components/business/VideoCall";

function AppInner() {
  const location = useLocation();
  const { user, subscription, loading } = useAuth();
  const { t } = useLang();
  const [payRedirecting, setPayRedirecting] = useState(() => !!localStorage.getItem("wz_pending_plan"));

  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) localStorage.setItem("wz_ref", ref.trim().toUpperCase());
  }, [location.search]);

  // Resume a subscription payment started from the public pricing cards before login.
  // Goes STRAIGHT to Paymob right after login (company onboarding is completed AFTER payment).
  useEffect(() => {
    if (loading || !user || user.kind === "employee" || user.kind === "marketer") return;
    const pending = localStorage.getItem("wz_pending_plan");
    if (!pending) { setPayRedirecting(false); return; }
    localStorage.removeItem("wz_pending_plan");
    setPayRedirecting(true);
    toast.loading("جارٍ تحويلك لإتمام الدفع…", { id: "resume-pay" });
    api.paymobSubscribe(pending)
      .then((d) => {
        if (d?.checkout_url) window.location.assign(d.checkout_url);
        else { toast.error("تعذّر بدء الدفع، افتح صفحة الأسعار وحاول مجدداً", { id: "resume-pay" }); setPayRedirecting(false); }
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "تعذّر بدء الدفع", { id: "resume-pay" });
        setPayRedirecting(false);
      });
  }, [loading, user]);

  // Detect OAuth callback synchronously (before auth gate) using reactive hash.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 font-body">
        {t("common.loading")}
      </div>
    );
  }

  if (!user) return <Landing />;

  if (user.kind === "employee") return <EmployeePortal />;

  if (user.kind === "marketer") return <MarketerPortal />;

  if (user.step_up_required) return <StepUpGate />;

  const locked = subscription && subscription.status === "expired" && !subscription.is_platform_owner;
  if (locked) return <Paywall />;

  if (payRedirecting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-500 font-body">
        <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-slate-700 animate-spin" />
        <p>جارٍ تحويلك لإتمام الدفع…</p>
      </div>
    );
  }

  if (user.needs_phone === true) return <PhoneGate />;

  if (subscription && subscription.needs_onboarding && !subscription.is_platform_owner && user.role === "manager") return <Onboarding />;

  return <Dashboard />;
}

{
  const t = new URLSearchParams(window.location.search).get("tab");
  if (t && /^[a-z-]{2,30}$/.test(t)) sessionStorage.setItem("portal_initial_tab", t);
}

function App() {
  return (
    <div className="App min-h-screen bg-slate-50 text-slate-900 font-body overflow-x-hidden">
      <Toaster position="top-center" richColors closeButton dir="rtl" />
      <NativeSelectSearch />
      <VideoCallHost />
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <ErrorBoundary>
            <Routes>
              <Route path="/meet/:code" element={<GuestMeet />} />
              <Route path="/verify/:id" element={<VerifyLetter />} />
              <Route path="/c/:id" element={<PublicCustody />} />
              <Route path="/card/:token" element={<PublicRiderCard />} />
              <Route path="/scan/:partId" element={<ScanConsume />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/employee" element={<EmployeeLanding />} />
              <Route path="/marketing" element={<MarketerLanding />} />
              <Route path="/insurer" element={<InsurerPortal />} />
              <Route path="/finance/*" element={<FinanceApp />} />
              <Route path="/pay" element={<PayCheckout />} />
              <Route path="/payment-result" element={<PaymentResult />} />
              <Route path="/page/:slug" element={<InfoPage />} />
              <Route path="/m/moto-maintenance" element={<MotoMaintenancePage />} />
              <Route path="/demo" element={<DemoEntry />} />
              <Route path="/corr/:id" element={<CorrespondencePortal />} />
              <Route path="/contract/:token" element={<ContractSign />} />
              <Route path="/warehouse/:token" element={<WarehousePortal />} />
              <Route path="/workshop/:token" element={<WorkshopPortal />} />
              <Route path="/platform" element={<PlatformConsole />} />
              <Route path="*" element={<AppInner />} />
            </Routes>
            </ErrorBoundary>
            <AssistantGate />
            <TutorialGate />
            <ActionGuard />
            <WaExportPrompt />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
